import axios from 'axios';
import CryptoJS from 'crypto-js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAppConfig, updateAppConfig } from '../services/configService';
import CookieService from '../services/CookieService';
import ActionBar from './ActionBar';
import InvoiceTable from './InvoiceTable';

const InvoiceContent = ({ onDataChange, filterValues, onFilterChange }) => {
  const [invoiceDataItems, setInvoiceDataItems] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const tableRef = useRef(null);

  // Authentication handler - runs on every component mount
  const handleDailyAuthentication = useCallback(async () => {

    try {
      // Get app configuration
      const config = await getAppConfig('einvoice', 'standalone');
      const settings = config?.settings || {};
      const taxAgencySettings = settings.taxAgencySettings || {};

      // Extract required fields
      const baseURL = taxAgencySettings.baseURL;
      const appKey = taxAgencySettings.appKey;
      const appSecret = taxAgencySettings.appSecret;
      const salt = taxAgencySettings.salt || '';
      const userAccount = taxAgencySettings.userAccount;
      const password = taxAgencySettings.password;

      // Validate required fields
      if (!baseURL || !appKey || !appSecret || !userAccount || !password) {
        console.log('Authentication skipped: missing required fields');
        return;
      }

      // Encrypt password: MD5 first, then SHA-1
      const md5Password = CryptoJS.MD5(password + salt).toString(CryptoJS.enc.Hex).toLowerCase();
      const sha1Password = CryptoJS.SHA1(md5Password).toString(CryptoJS.enc.Hex);

      // Current timestamp
      const timestamp = Date.now().toString();

      // Prepare params for token request
      const params = {
        timestamp: timestamp,
        method: 'baiwang.oauth.token',
        grant_type: 'password',
        version: '6.0',
        client_id: appKey,
      };

      // Prepare body for token request
      const body = {
        password: sha1Password,
        username: userAccount,
        client_secret: appSecret
      };

      // Get token from CookieService
      const token = CookieService.getToken();
      if (!token) {
        console.log('Authentication skipped: user not logged in');
        return;
      }

      // Prepare headers
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token && token.trim() !== '') {
        headers['authorization'] = `Bearer ${token}`;
      }

      // Send authentication request via API proxy
      const response = await axios.post('/api-proxy', {
        targetUrl: baseURL,
        method: 'POST',
        data: body,
        params: params,
        headers: headers
      }, {
        headers
      });

      if (response.data && response.data.response && response.data.response.access_token) {
        const newToken = response.data.response.access_token;

        // Update configuration with new token - preserve all existing settings
        const updatedSettings = {
          themeSetting: settings.themeSetting || {},
          serverSettings: settings.serverSettings || {},
          taxAgencySettings: {
            ...taxAgencySettings,
            token: newToken
          },
          processSettings: settings.processSettings || {},
          companyInfo: settings.companyInfo || {}
        };

        // Save updated configuration
        await updateAppConfig(updatedSettings, 'einvoice');

        console.log('Authentication completed successfully');
      } else {
        console.error('Authentication failed: No access_token in response');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      // Silently fail - don't show error messages for background operations
    }
  }, []);

  // Run authentication on component mount
  useEffect(() => {
    handleDailyAuthentication();
  }, [handleDailyAuthentication]);

  // Handle when invoice selection changes
  const handleSelectionChange = (selectedInvoiceIds, selectedRows) => {
    setSelectedInvoices(selectedRows);
  };

  // Use useCallback to prevent re-creation of this function on every render
  const handleDataChange = useCallback((data) => {
    setInvoiceDataItems(data.items || []);

    // Only call parent onDataChange if actually needed
    if (onDataChange) {
      onDataChange(data);
    }
  }, [onDataChange]);

  // Create a refresh handler that calls the fetchData method on the table
  const handleRefresh = useCallback(() => {
    if (tableRef.current) {
      tableRef.current.fetchData();
      tableRef.current.clearSelection();
      setSelectedInvoices([]);
    }
  }, []);

  return (
    <div className="main-content">
      <ActionBar
        selectedInvoices={selectedInvoices}
        onRefresh={handleRefresh}
        currentStatus={filterValues?.status}
      />
      <InvoiceTable
        ref={tableRef}
        onDataChange={handleDataChange}
        filterValues={filterValues}
        onFilterChange={onFilterChange}
        onSelectionChange={handleSelectionChange}
      />
    </div>
  );
};

export default InvoiceContent; 